import { getUnixTime, isBefore } from 'date-fns';

import { VisitStatus } from './_types';

import type { Visit } from './_types';
import type { Appointment } from 'src/models/appointment/_types';
import type { User } from 'src/models/user/_types';
import type { Timestamp } from 'src/types/utilityTypes';

export const getVisitTitle = (visit: Visit): string => {
  if (visit.appointment) {
    return visit.appointment.serviceName;
  }
  return 'Open Gym Visit';
};

export const getReadableVisitStatus = (status?: VisitStatus | string): string => {
  if (!status) {
    return '';
  }
  const mapping: Record<string, string> = {
    [VisitStatus.Reserved]: 'Reserved',
    [VisitStatus.Registered]: 'Confirmed',
    [VisitStatus.Completed]: 'Checked In',
    [VisitStatus.Cancelled]: 'Early cancel',
    [VisitStatus.NoShowed]: 'No show',
    [VisitStatus.LateCancelled]: 'Late cancel',
  };
  if (mapping[status]) {
    return mapping[status];
  }
  const firstLetter = String(status).charAt(0);
  const rest = String(status).slice(1);
  return `${firstLetter.toUpperCase()}${rest}`;
};

export const getVisitStatus = (visit: Visit): string => {
  return getReadableVisitStatus(visit.status);
};

export const getVisitStartTime = (visit: Visit): Timestamp => {
  if (visit.appointment && visit.completedAt) {
    return visit.completedAt;
  }
  if (visit.checkIn) {
    return visit.checkIn;
  }
  if (visit.createdAt) {
    return visit.createdAt;
  }
  throw new Error('Completed visit without checkin or completed time');
};

export const isCheckedIn = (visit: Visit): boolean => {
  return visit.status === VisitStatus.Completed;
};

export const isNoShowed = (visit: Visit): boolean => {
  return visit.status === VisitStatus.NoShowed;
};

export const isCanceled = (visit: Visit): boolean => {
  return (
    visit.status === VisitStatus.Cancelled ||
    visit.status === VisitStatus.LateCancelled
  );
};

export const isClosedOut = (visit: Visit): boolean => {
  return (
    visit.status === VisitStatus.Completed ||
    visit.status === VisitStatus.NoShowed ||
    visit.status === VisitStatus.LateCancelled ||
    visit.status === VisitStatus.Cancelled
  );
};

export const isExpired = (visit: Visit): boolean => {
  if (!visit.appointment) {
    return false;
  }
  return isBefore(visit.appointment.endTime, getUnixTime(new Date()));
};

export const isGuest = (visit: Visit): boolean => {
  return !!visit.chaperoneVisitId;
};

export const getVisitChaperone = (
  visit: Visit,
  appointment: Appointment
): User | undefined => {
  if (isGuest(visit)) {
    const chaperoneVisit = appointment.visits.find(
      (v) => v.id == visit.chaperoneVisitId
    );
    return chaperoneVisit?.user;
  }
  return undefined;
};

export const guestVisits = (visit: Visit, visits: Visit[]): Visit[] => {
  return visits.filter((v) => v.id == visit.chaperoneVisitId);
};

export const isCheckIn = (visit: Visit): boolean => {
  return !!visit.checkIn && !visit.appointmentId;
};


